// Reac Component
import React from 'react'
import { Link, useSearchParams, useNavigate } from "react-router-dom"
import { useTranslation } from 'react-i18next'

// App Component
import { FamlaServices } from '../../services'
import { Buttons, InputForm } from '../../components'
import { Routes } from '../../routes'
import './UsersLogin.css'

// Features
import { iAlert } from '../../interfaces'
import { trackUiButtonEvent, UI_BUTTON_EVENTS } from '../../services/api/uiButtonStatsService'
// import { useAppDispatch } from '../../stores'

interface iState {
    newPassword: string,
    password: string,
    isSubmit: boolean,
    isValidMail: boolean,
    verifyCode: string | null
}

/** Clé sessionStorage : évite de perdre le token si l’URL est nettoyée (ex. React Strict Mode double montage). */
const RESET_VERIFY_TOKEN_KEY = 'famla_reset_verify_token'

function readInitialVerifyCode(): string {
    if (typeof window === 'undefined') return ''
    try {
        const fromUrl = new URLSearchParams(window.location.search).get('pwd')
        if (fromUrl) {
            sessionStorage.setItem(RESET_VERIFY_TOKEN_KEY, fromUrl)
            return fromUrl
        }
        return sessionStorage.getItem(RESET_VERIFY_TOKEN_KEY) || ''
    } catch {
        return ''
    }
}

const ResetLastPassword = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [user, setUser] = React.useState<iState>(() => ({
        newPassword: '',
        password: '',
        isSubmit: false,
        isValidMail: true,
        verifyCode: readInitialVerifyCode(),
    }))

    const [alert, setAlert] = React.useState<iAlert>({
        isActive: false,
        type: '',
        message: ''
    })
    const [searchParams, setSearchParams] = useSearchParams()

    React.useEffect(() => {
        const pwd = searchParams.get('pwd')
        if (pwd) {
            try {
                sessionStorage.setItem(RESET_VERIFY_TOKEN_KEY, pwd)
            } catch {
                /* ignore */
            }
            setUser((prev) => ({ ...prev, verifyCode: pwd }))
            setSearchParams({}, { replace: true })
        }
    }, [searchParams, setSearchParams])



    const handleSubmit = async (event: React.SyntheticEvent) => {
        event.preventDefault();

        const token = user.verifyCode?.trim() || ''
        if (!token) {
            setAlert({
                isActive: true,
                type: 'danger',
                message: t('views.auth.resetLinkInvalidOrExpired'),
            })
            return
        }

        if (user.newPassword.length === 0) {
            setAlert({ isActive: true, type: 'danger', message: t('views.auth.newPassWordIsRequired') });
            return;
        }

        if (user.password !== user.newPassword) {
            setAlert({ isActive: true, type: 'danger', message: t('views.auth.passwordsNotMatch') });
            return
        }

        trackUiButtonEvent(UI_BUTTON_EVENTS.auth.resetNewPasswordSubmit);
        let formData = {
            verify_code: token,
            passwordConfirm: user.newPassword,
            password: user.password,
        }
        setUser({ ...user, isSubmit: true });
        let data = await FamlaServices("api_host", "/auth/reset-password", "POST", formData, "");

        if (data.status === 201) {
            try {
                sessionStorage.removeItem(RESET_VERIFY_TOKEN_KEY)
            } catch {
                /* ignore */
            }
            setUser((prev) => ({ ...prev, newPassword: '', password: '', isSubmit: false, isValidMail: true }));
            setAlert({ isActive: true, type: 'success', message: t('views.auth.passwordSuccessfully') });
            navigate(Routes.AUTH)
        }
        else if (data.body.status_code === 400 && data.body.description === "user does not exist") {
            setAlert({ isActive: true, type: 'danger', message: t('views.auth.userDoesNotExist') });
            setUser({ ...user, newPassword: user.newPassword, password: '', isSubmit: false });
        } else if (data.body.status_code === 400) {
            setAlert({ isActive: true, type: 'danger', message: t('views.auth.passwordsNotMatch') });
            setUser({ ...user, newPassword: user.newPassword, password: '', isSubmit: false });
        }
        else {
            setUser({ ...user, newPassword: user.newPassword, password: '', isSubmit: false });
            setAlert({ isActive: true, type: 'danger', message: t('views.auth.anErrorOcured') });
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { type, name, value, checked } = event.target;
        let fieldValue: any;
        if (type === 'checkbox') {
            fieldValue = checked;
        } else {
            fieldValue = value;
        }
        if (name === "password") {
            setUser({ ...user, password: fieldValue })

        } else if (name === "newPassword") {
            setUser({ ...user, newPassword: fieldValue })
        }


    };


    return (
        <div className="users-login-light">
            <div className="users-login-title-block">
                <h1 className="users-login-headline">{t('views.auth.resetYourPassword')}</h1>
            </div>
            <form onSubmit={handleSubmit}>
                {alert.isActive &&
                    <div className={`alert alert-${alert.type}`} role="alert">
                        {alert.message}
                    </div>
                }

                <InputForm
                    inputType="password"
                    inputLabel={t('views.auth.newPassword')}
                    inputName="password"
                    inputValue={user.password}
                    isFloatingLabel={true}
                    labelClass="text-theme"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
                    inputContainerClass={'mb-3'}
                    withIcon={true}
                    iconClass="text-theme"
                />
                <InputForm
                    inputType="password"
                    inputLabel={t('views.auth.confirmNewPassword')}
                    inputName="newPassword"
                    inputValue={user.newPassword}
                    isFloatingLabel={true}
                    labelClass="text-theme"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
                    inputContainerClass={'mb-3'}
                    withIcon={true}
                    iconClass="text-theme"
                />
                <Buttons
                    name={t('views.auth.resetYourPassword')}
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => { handleSubmit(event) }}
                    isLoading={user.isSubmit}
                    btnClass="users-login-submit w-100"
                    arrowVisible={false}
                    labelClass="text-white fw-bold"
                    styleProps={{ minHeight: 48 }}
                    arrowColor=''
                />
            </form>

            <p className="users-login-signup-footer mb-0">
                {t('views.auth.dontHaveAccount')}{' '}
                <Link to={Routes.AUTH_LOGIN} className="users-login-terracotta-link fw-medium">{t('views.auth.logIn')}</Link>
            </p>
        </div>
    )
}

export default ResetLastPassword;